import { defineStore } from "pinia";
import {
  fromUnixToAPIDate,
  getEndDate,
  getStartDate,
  toUnix,
} from "@/stories/utils/dates";
import joi from "joi";
import { useAuthStore } from "@/stores/authStore";
import md5 from "md5";
import type { IVideoState } from "@/interfaces";
import type {
  EditRepeatTypes,
  StoreName,
  TabErrorSchemaTypes,
  EditorModel,
  IdType,
  RRuleSupportedEntityType,
  DefaultFilterType,
  DefaultState,
  PreviewBasedState,
} from "@/types";
import helpers from "@/helpers";
import { fixValidationFields, validate } from "@/helpers/forms/validate";
import { globali18n } from "@/i18n";
import {
  type NewsInput,
  Group_Connection,
  Module_Type,
  type NewsUnion,
  type CompactInstitutionFragment,
} from "@/graphql/generated-types";
import api from "@/api";
import PagerManager from "@/helpers/manager/PagerManager";
import { IS_IBG_MANAGER } from "@/consts";
import type { SummarizedNews } from "@/graphql";
import getMaxDescriptionLength from "@/helpers/environment/getMaxDescriptionLength";
import toVideoList from "@/helpers/files/toVideoList";
import {
  PDF_FEATURE,
  type PDFFeatureState,
} from "@/helpers/features/PDFFeature";
import {
  PICTURES_FEATURE,
  type PicturesFeatureState,
} from "@/helpers/features/PicturesFeature";
import {
  SHARING_FEATURE,
  type SharingFeatureState,
} from "@/helpers/features/SharingFeature";
import {
  EXTERNAL_EVENT_FEATURE,
  type ExternalEventFeatureState,
} from "@/helpers/features/ExternalEventFeature";
import getTabCounts from "@/helpers/shared/getTabCounts";
import {
  RRULE_FEATURE,
  type RRuleFeatureState,
} from "@/helpers/features/RRuleFeature";
import toPreviewBasedState from "@/helpers/shared/toPreviewBasedState";

const VALIDATION_SCHEMA = joi.object({
  title: joi.string().max(50).required(),
  description: joi.string().max(getMaxDescriptionLength()).required(),
  startDate: joi.number().required(),
  endDate: joi.number().required().greater(joi.ref("startDate")),
  imageList: joi.array().min(1).max(30),
  pdf: joi
    .array()
    .optional()
    .items(
      joi.object({
        name: joi.string().required().max(50).min(1),
      }),
    ),
});

const TAB_FIELDS_SCHEMA: TabErrorSchemaTypes = {
  coreData: ["title", "body", "endDate"],
  pictures: ["imageList"],
  documents: ["pdf.*.name"],
};

export type NewsItem = {
  id?: number;
  title: string;
  body: EditorModel;
  startDate: number;
  pictograms: string[];
  institution?: CompactInstitutionFragment;
  videos: IVideoState[];
  documents?: string[] | null | undefined;
  category?: string;
  editRepeat: EditRepeatTypes;
  repeatId?: string;
  rrule: string;
  isCancelled: boolean;
  __typename: RRuleSupportedEntityType;
  original: Partial<NewsItem>;
} & PDFFeatureState &
  PicturesFeatureState &
  Omit<RRuleFeatureState, "original"> &
  Omit<SharingFeatureState, "original">;

export type NewsListFilters = {
  categories: PreviewBasedState<string[]>;
  departments: PreviewBasedState<string[]>;
  groups: PreviewBasedState<string[]>;
};

const getDefaultListFilters = (): NewsListFilters => {
  return {
    categories: toPreviewBasedState([]),
    departments: toPreviewBasedState([]),
    groups: toPreviewBasedState([]),
  };
};

export type NewsState = {
  listFilters: NewsListFilters;
  item: NewsItem;
} & DefaultState &
  ExternalEventFeatureState<SummarizedNews>;

const getDefaultItem = (): NewsItem => {
  return {
    title: "",
    body: helpers.quill.getEmptyEditorModel(),
    pictograms: [],
    videos: [],
    documents: [],
    category: undefined,
    rrule: "",
    isCancelled: false,
    original: {
      startDate: getStartDate(),
      endDate: getEndDate(),
    },
    ...PDF_FEATURE.getDefault(),
    ...PICTURES_FEATURE.getDefault(),
    ...SHARING_FEATURE.getDefault(),
    ...RRULE_FEATURE.getDefault("SingleNews"),
  };
};

const getDefaultState = (): NewsState => {
  return {
    listFilters: getDefaultListFilters(),
    tabErrors: {},
    originalHash: null,
    validation: null,
    item: {
      ...getDefaultItem(),
    },
    pager: new PagerManager<SummarizedNews>(),
    listResourceType: "internal",
  };
};

export const createUseStore = (id: StoreName) => {
  return defineStore(id, {
    state: (): NewsState => getDefaultState(),
    getters: {
      isSharedWithGroups: (state) =>
        SHARING_FEATURE.isSharedWithGroups(state.item),
      tabCounts: (state) => getTabCounts(state),
    },
    actions: {
      hasUnsavedChanges() {
        return !!(
          this.item &&
          this.originalHash &&
          this.originalHash !== this.currentHash()
        );
      },
      currentHash(): string {
        const data = {
          ...this.getItem(),
          videos: this.item.videos,
          ...PDF_FEATURE.hash(this.item),
          ...PICTURES_FEATURE.hash(this.item),
          ...SHARING_FEATURE.hash(this.item),
          ...RRULE_FEATURE.hash(this.item as RRuleFeatureState),
        };

        return md5(JSON.stringify(data));
      },
      setOriginalHash() {
        // It can take times to TextEditorInput convert html to delta.
        // That's why there is a timeout here.
        this.originalHash = this.currentHash();
      },
      unselectItem() {
        this.item = { ...getDefaultItem() };
        this.setOriginalHash();
      },
      async setItem(id: IdType) {
        // No need to refetch
        if (id && this.item.id?.toString() === id.toString()) {
          return;
        }

        let data: NewsUnion | undefined;

        if (this.listResourceType === "internal") {
          data = await api.news.getNewsDetail(id);
        } else {
          data = await api.news.getExternalNewsDetail(id);
        }

        if (!data) {
          return;
        }

        this.item = {
          ...getDefaultState().item,
          id: parseInt(data.id),
          title: data.title,
          startDate: toUnix(new Date(data.startDate)),
          endDate: toUnix(new Date(data.endDate)),
          body: helpers.quill.htmlToEditorModel(data.body || ""),
          pictograms: (data.pictograms || [])?.map((item) => item.id),
          documentToUpload: [],
          videos: toVideoList(data),
          rrule: "",
          category: data.category?.id || undefined,
          editRepeat: "single" as EditRepeatTypes,
          __typename: data.__typename as RRuleSupportedEntityType,
          isCancelled: data.cancelled,
          institution: data.institution,
          original: {
            startDate: toUnix(new Date(data.startDate)),
            endDate: toUnix(new Date(data.endDate)),
          },
          ...SHARING_FEATURE.getDefault(),
          ...PICTURES_FEATURE.getState(data),
        };

        SHARING_FEATURE.setState({
          data,
          state: this.item,
          moduleType: Module_Type.News,
          groupConnection: Group_Connection.News,
          listResourceType: this.listResourceType,
          onCompleted: this.setOriginalHash,
        });

        RRULE_FEATURE.setState(data, this.item as RRuleFeatureState);

        PDF_FEATURE.setState(data, this.item);

        this.setOriginalHash();
        this.validation = null;
      },
      getItem(): NewsInput {
        const item = { ...this.item };
        const authStore = useAuthStore();

        let image = item.image;
        if (typeof image === "object") {
          image = image.id;
        }

        return {
          description: helpers.quill.editorModelToHtml(item.body),
          endDate: fromUnixToAPIDate(item.endDate),
          id: item?.id?.toString(),
          institution:
            item.institution?.id?.toString() || authStore.institutionId,
          startDate: fromUnixToAPIDate(item.startDate),
          status: item.isCancelled ? 2 : 1,
          title: item.title,
          image: image as string | null | undefined,
          pictograms: item.pictograms,
          documents: item.documents,
          category: item.category || null,
          ...SHARING_FEATURE.getItem(this.item),
          ...RRULE_FEATURE.getItem(this.item as RRuleFeatureState),
        };
      },
      async isNotValid() {
        this.validation = validate(VALIDATION_SCHEMA, {
          ...this.item,
          description: helpers.quill.editorModelToText(this.item.body),
          pdf: IS_IBG_MANAGER
            ? this.item.documentToUpload.filter(
                (item) => item.action !== "delete",
              )
            : [],
          ...PICTURES_FEATURE.toValidation(this.item),
        });

        // We can't use usei18n() in stores.
        // https://stackoverflow.com/a/71449708
        const { t } = globali18n;

        // We should fix the validation labels
        fixValidationFields(this.validation, {
          startDate: t("global.visible_from"),
        });

        this.tabErrors = helpers.forms.getTabErrors(
          TAB_FIELDS_SCHEMA,
          this.validation.errors,
        );

        return !this.validation.isValid;
      },
      async fetchItems(filter: DefaultFilterType) {
        await EXTERNAL_EVENT_FEATURE.fetchItems(
          this,
          filter,
          api.news.getNews,
          api.news.getExternalNews,
        );
      },
    },
  });
};

// News View store
export const useNewsStore = createUseStore("NEWS");

// We use the same store structure for the create modal.
export const useAddNewsStore = createUseStore("ADD_NEWS");

export type NewsStore = ReturnType<typeof useNewsStore>;
